import type { ReactNode } from 'react';

import { HintedIcon } from '../../../chrome/hint';
import { Chip } from '../../../ui/chip';
import type { LedgerStateValue } from './api/schemas';
import { chipStatusIcon, jobTypeIcon } from './jobTypeIcons';
import { cm } from './messages';

/** Literal keys into the catalogue, called fresh (not memoised) so a locale switch is honoured --
 *  a `job_type` this screen does not recognise (the API names no closed set) falls back to the
 *  raw value rather than a missing-key crash. */
export function jobTypeLabel(jobType: string): string {
  switch (jobType) {
    case 'posts':
      return cm('jobType.posts');
    case 'comments':
      return cm('jobType.comments');
    case 'stories':
      return cm('jobType.stories');
    case 'audio_fanout':
      return cm('jobType.audio_fanout');
    case 'profile_tracked':
      return cm('jobType.profile_tracked');
    default:
      return jobType;
  }
}

/** A blocked campaign's unfinished slots will not run whatever `state` says today, so they read
 *  "sem crédito" instead of promising a collection. */
export function isNoCredit(state: LedgerStateValue, blocked: boolean): boolean {
  return blocked && (state === 'planned' || state === 'dispatched');
}

export function statusLabel(state: LedgerStateValue, blocked: boolean): string {
  if (isNoCredit(state, blocked)) return cm('status.noCredit');
  switch (state) {
    case 'planned':
      return cm('status.planned');
    case 'dispatched':
      return cm('status.dispatched');
    case 'done':
      return cm('status.done');
    case 'failed':
      return cm('status.failed');
  }
}

const PENDING_DISC = 'bg-muted-foreground text-background';

const STATUS_DISC: Record<LedgerStateValue, string> = {
  planned: PENDING_DISC,
  dispatched: PENDING_DISC,
  done: 'bg-primary text-primary-foreground',
  failed: 'bg-destructive text-destructive-foreground',
};

/** One job type at one slot: the status in its own disc, then the job type's icon. The disc's
 *  colour repeats what the icon and the accessible name already say, so it is never the only
 *  signal. */
export function SlotChip(props: {
  jobType: string;
  state: LedgerStateValue;
  blocked: boolean;
}): ReactNode {
  const StatusIcon = chipStatusIcon(props.state, props.blocked);
  const KindIcon = jobTypeIcon(props.jobType);
  const disc = isNoCredit(props.state, props.blocked) ? PENDING_DISC : STATUS_DISC[props.state];
  return (
    <HintedIcon
      label={`${jobTypeLabel(props.jobType)}: ${statusLabel(props.state, props.blocked)}`}
    >
      <Chip tone="neutral" size="sm" className="gap-1 pl-0.5">
        <span className={`inline-flex size-4 items-center justify-center rounded-full ${disc}`}>
          <StatusIcon aria-hidden className="size-2.5" />
        </span>
        <KindIcon aria-hidden className="text-secondary-foreground" />
      </Chip>
    </HintedIcon>
  );
}
